package org.example.project.ui.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.ActivityStatus
import org.example.project.ui.components.MetadataTag
import org.example.project.ui.components.StatusBadge
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.EditorColors

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ActivityCardItem(
    activity: ActivityResponse,
    isMutatingDisabled: Boolean,
    onEdit: () -> Unit,
    onPublish: () -> Unit,
    modifier: Modifier = Modifier
) {
    val isDraft = activity.status == ActivityStatus.DRAFT

    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = EditorColors.Surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        shape = RoundedCornerShape(16.dp),
        border = BorderStroke(1.dp, EditorColors.BorderSubtle)
    ) {
        Column(modifier = Modifier.padding(18.dp)) {
            // Header Row: Title + Status + Action buttons
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f).padding(end = 12.dp)
                ) {
                    Text(
                        text = activity.name,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = EditorColors.TextPrimary
                    )
                    Spacer(modifier = Modifier.width(10.dp))
                    StatusBadge(status = activity.status)
                }

                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    OutlinedButton(
                        onClick = onEdit,
                        enabled = !isMutatingDisabled,
                        shape = RoundedCornerShape(8.dp),
                        border = BorderStroke(1.dp, EditorColors.Border),
                        contentPadding = ButtonDefaults.ContentPadding
                    ) {
                        Icon(
                            imageVector = AppIcons.Edit,
                            contentDescription = null,
                            tint = if (isMutatingDisabled) EditorColors.TextDisabled else EditorColors.TextSecondary,
                            modifier = Modifier.size(14.dp)
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(
                            text = "Edytuj",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                            color = if (isMutatingDisabled) EditorColors.TextDisabled else EditorColors.TextSecondary
                        )
                    }

                    if (isDraft) {
                        Button(
                            onClick = onPublish,
                            enabled = !isMutatingDisabled,
                            shape = RoundedCornerShape(8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = EditorColors.SuccessSolid,
                                contentColor = EditorColors.Surface
                            ),
                            contentPadding = ButtonDefaults.ContentPadding
                        ) {
                            Icon(
                                imageVector = AppIcons.Check,
                                contentDescription = null,
                                tint = EditorColors.Surface,
                                modifier = Modifier.size(14.dp)
                            )
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = "Opublikuj",
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Metadata Tags Row
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                MetadataTag(label = "Kategoria", value = activity.category)
                MetadataTag(label = "Wiek", value = "${activity.minAge}–${activity.maxAge} lat")
                MetadataTag(label = "Czas", value = "${activity.durationMinutes} min")
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Description
            Text(
                text = activity.description,
                fontSize = 13.sp,
                color = EditorColors.TextSecondary,
                lineHeight = 18.sp,
                maxLines = 3
            )
        }
    }
}
